import React, { useState } from "react";
import {
  Page,
  LegacyCard,
  TextField,
  Button,
  FormLayout,
  Banner,
  Text,
  InlineStack,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatPlate } from "../../utility/funcs";
import ClientSearch from "../CustomComponents/ClientSearch/ClientSearch";

function NewInfraction() {
  const navigate = useNavigate();
  const { session } = UserAuth();

  const [date, setDate] = useState("");
  const [plate, setPlate] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookup, setLookup] = useState(null);
  // lookup = { car, reservation, client: { type: "customer"|"company", id, label } | null }

  const [searchOpen, setSearchOpen] = useState(false);

  const [saving, setSaving] = useState(false);

  const runLookup = async () => {
    setLookupError("");
    setLookup(null);
    if (!date || !plate.trim()) {
      setLookupError("Ingresa fecha y placa.");
      return;
    }
    setLookupLoading(true);
    const { data: cars, error: carsErr } = await supabase
      .from("cars")
      .select("id, plate, model:model(name, brand:brand(name))")
      .ilike("plate", plate.trim());
    if (carsErr) {
      setLookupError(carsErr.message);
      setLookupLoading(false);
      return;
    }
    if (!cars || cars.length === 0) {
      setLookupError(`No se encontró auto con placa "${plate.trim()}".`);
      setLookupLoading(false);
      return;
    }
    const car = cars[0];

    // tstzrange contains check — Postgres handles this with @>
    // Send the date as 'YYYY-MM-DD' and use a contained-by filter via RPC-less approach.
    // Easiest: use overlaps with a single-day range.
    const start = `${date} 00:00:00+00`;
    const end = `${date} 23:59:59+00`;
    const { data: reservations, error: resErr } = await supabase
      .from("reservations")
      .select(
        "id, customer:customer(id, first_name, last_name), company:company(id, name), date_range, status"
      )
      .eq("car", car.id)
      .overlaps("date_range", `[${start},${end}]`)
      .neq("status", "cancelled");

    if (resErr) {
      setLookupError(resErr.message);
      setLookupLoading(false);
      return;
    }

    const reservation = reservations?.[0] || null;
    let client = null;
    if (reservation?.customer) {
      client = {
        type: "customer",
        id: reservation.customer.id,
        label: `${reservation.customer.first_name || ""} ${reservation.customer.last_name || ""}`.trim(),
      };
    } else if (reservation?.company) {
      client = {
        type: "company",
        id: reservation.company.id,
        label: reservation.company.name,
      };
    }
    setLookup({
      car,
      reservation: reservation ? { id: reservation.id } : null,
      client,
    });
    setLookupLoading(false);
  };

  const pickClient = (r) => {
    setLookup((prev) => ({
      ...(prev || {}),
      client: { type: r.client_type, id: r.id, label: r.name },
    }));
    setSearchOpen(false);
  };

  const canSave =
    lookup?.car &&
    lookup?.client &&
    parseFloat(amount) > 0 &&
    reason.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const { data, error } = await supabase
      .from("infractions")
      .insert({
        infraction_date: date,
        plate: plate.trim().toUpperCase(),
        car_id: lookup.car.id,
        reservation_id: lookup.reservation?.id || null,
        customer_id: lookup.client.type === "customer" ? lookup.client.id : null,
        company_id: lookup.client.type === "company" ? lookup.client.id : null,
        amount: parseFloat(amount),
        reason: reason.trim(),
        created_by: session?.user?.id || null,
      })
      .select("id")
      .single();
    setSaving(false);
    if (error) {
      alert("Error: " + error.message);
      return;
    }
    navigate(`/infractions/${data.id}`);
  };

  return (
    <AppWrapper>
      <Page
        title="Nueva Infracción"
        backAction={{ onAction: () => navigate("/infractions") }}
      >
        <LegacyCard sectioned>
          <FormLayout>
            <FormLayout.Group>
              <TextField
                label="Fecha de la infracción"
                type="date"
                value={date}
                onChange={setDate}
                autoComplete="off"
              />
              <TextField
                label="Placa"
                value={plate}
                onChange={(v) => setPlate(v.toUpperCase())}
                autoComplete="off"
                placeholder="ABC1234"
              />
            </FormLayout.Group>
            <Button onClick={runLookup} loading={lookupLoading} variant="primary">
              Buscar Reserva
            </Button>

            {lookupError && <Banner tone="critical">{lookupError}</Banner>}

            {lookup && (
              <LegacyCard sectioned>
                <Text variant="headingSm">Auto</Text>
                <Text variant="bodySm">
                  {lookup.car.model?.brand?.name} {lookup.car.model?.name} ·{" "}
                  {formatPlate(lookup.car.plate)}
                </Text>

                <div style={{ marginTop: 12 }}>
                  <Text variant="headingSm">Reserva</Text>
                  {lookup.reservation ? (
                    <Text variant="bodySm">#{lookup.reservation.id}</Text>
                  ) : (
                    <Text variant="bodySm" tone="subdued">
                      Sin reserva activa en esa fecha — selecciona cliente
                      manualmente.
                    </Text>
                  )}
                </div>

                <div style={{ marginTop: 12 }}>
                  <Text variant="headingSm">Cliente</Text>
                  {lookup.client ? (
                    <InlineStack gap="200" blockAlign="center">
                      <Text variant="bodySm">
                        {lookup.client.label}
                        {lookup.client.type === "company" ? " (Empresa)" : ""}
                      </Text>
                      {!lookup.reservation && (
                        <Button
                          variant="plain"
                          onClick={() => setSearchOpen(true)}
                        >
                          Cambiar
                        </Button>
                      )}
                    </InlineStack>
                  ) : (
                    <Button onClick={() => setSearchOpen(true)}>
                      Buscar Cliente
                    </Button>
                  )}
                </div>
              </LegacyCard>
            )}

            <FormLayout.Group>
              <TextField
                label="Monto"
                type="number"
                value={amount}
                onChange={setAmount}
                prefix="$"
                autoComplete="off"
              />
              <TextField
                label="Motivo"
                value={reason}
                onChange={setReason}
                multiline={2}
                autoComplete="off"
                placeholder="Exceso de velocidad, mal estacionamiento, etc."
              />
            </FormLayout.Group>

            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!canSave}
              loading={saving}
            >
              Guardar Infracción {amount && `(${formatPrice(parseFloat(amount) || 0)})`}
            </Button>
          </FormLayout>
        </LegacyCard>

        <ClientSearch
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelect={pickClient}
        />
      </Page>
    </AppWrapper>
  );
}

export default NewInfraction;
